import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Clock, MapPin, DollarSign, Users } from "lucide-react";
import { ClassCard } from "./Home";

export function ClassesList() {
  const [classes, setClasses] = useState([]);
  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).finally(() => setLoading(false));
  }, []);

  const byMonth = classes.reduce((acc, c) => {
    const key = new Date(c.date).toLocaleString("en-US", { month: "long", year: "numeric" });
    (acc[key] = acc[key] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-script text-3xl text-brand-coral">Register today</span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-brand-navy tracking-tight mt-2">Upcoming Classes</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Browse open classes by month. Reserve your seat instantly — waitlist available for full classes.</p>
        </div>
        <div className="inline-flex rounded-full border border-slate-200 p-1 bg-white" data-testid="view-toggle">
          <button onClick={() => setView("list")} data-testid="view-list" className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${view === "list" ? "bg-brand-navy text-white" : "text-brand-navy"}`}>List</button>
          <button onClick={() => setView("month")} data-testid="view-month" className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${view === "month" ? "bg-brand-navy text-white" : "text-brand-navy"}`}>By Month</button>
        </div>
      </div>

      {loading && <p className="text-slate-500 mt-8">Loading classes...</p>}
      {!loading && classes.length === 0 && (
        <div className="mt-12 bg-brand-surface rounded-2xl p-10 text-center">
          <p className="text-slate-600">No upcoming classes right now. Check back soon or <Link to="/group-training" className="text-brand-teal font-bold">request a private session</Link>.</p>
        </div>
      )}

      {view === "list" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="classes-list">
          {classes.map((c) => <ClassCard key={c.id} c={c} />)}
        </div>
      )}

      {view === "month" && (
        <div className="mt-10 space-y-12">
          {Object.entries(byMonth).map(([month, items]) => (
            <div key={month}>
              <h2 className="font-display text-2xl font-bold text-brand-navy border-b border-slate-200 pb-3">{month}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                {items.map((c) => <ClassCard key={c.id} c={c} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClassDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [klass, setKlass] = useState(null);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", seats: 1,
    additional_participants: "", organization: "", accessibility_needs: "",
    policy_agreement: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get(`/classes/${id}`).then((r) => setKlass(r.data)).catch(() => navigate("/classes"));
  }, [id, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.policy_agreement) { toast.error("Please agree to the cancellation policy"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/registrations", { ...form, class_id: id, seats: Number(form.seats) });
      setConfirmed(data);
      toast.success(data.status === "waitlist" ? "Added to waitlist!" : "Registration confirmed!");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!klass) return <div className="max-w-5xl mx-auto px-4 py-16 text-slate-500">Loading...</div>;

  if (confirmed) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="inline-block bg-brand-teal/10 text-brand-teal rounded-full px-4 py-1 text-sm font-semibold uppercase tracking-widest">
          {confirmed.status === "waitlist" ? "Waitlisted" : "Confirmed"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-brand-navy mt-4 tracking-tight">
          {confirmed.status === "waitlist" ? "You're on the waitlist!" : "You're all set!"}
        </h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          A confirmation email is on its way. Please arrive 10 minutes early and wear comfortable clothing.
          Contact info@kenmarqsafety.com for changes.
        </p>
        <Card className="mt-8 p-6 text-left bg-brand-surface border-none">
          <h3 className="font-display font-bold text-brand-navy">{klass.title}</h3>
          <div className="text-sm text-slate-600 mt-2 space-y-1">
            <div>{new Date(klass.date).toDateString()} · {klass.start_time}–{klass.end_time}</div>
            <div>{klass.location}</div>
          </div>
        </Card>
        <Button onClick={() => navigate("/classes")} className="mt-8 bg-brand-navy hover:bg-brand-navy text-white rounded-full px-8 btn-lift" data-testid="confirmation-back">Back to Classes</Button>
      </div>
    );
  }

  const deadlinePassed = new Date(klass.registration_deadline) < new Date();
  const isFull = klass.is_full || klass.seats_available <= 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Link to="/classes" className="inline-flex items-center gap-1 text-sm text-brand-navy hover:text-brand-teal font-semibold" data-testid="back-classes">
        <ArrowLeft className="h-4 w-4" /> Back to Classes
      </Link>

      <div className="grid lg:grid-cols-12 gap-10 mt-6">
        <div className="lg:col-span-5">
          <div className="bg-brand-navy text-white rounded-3xl p-8 relative overflow-hidden">
            <div className="text-xs uppercase tracking-widest font-semibold text-brand-amber">{klass.training_type}</div>
            <h1 className="font-display text-2xl md:text-3xl font-extrabold mt-2 tracking-tight">{klass.title}</h1>
            <p className="mt-3 text-white/80 text-sm leading-relaxed">{klass.description}</p>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex gap-3"><Calendar className="h-4 w-4 text-brand-teal" /> {new Date(klass.date).toDateString()}</div>
              <div className="flex gap-3"><Clock className="h-4 w-4 text-brand-teal" /> {klass.start_time} – {klass.end_time}</div>
              <div className="flex gap-3"><MapPin className="h-4 w-4 text-brand-teal" /> {klass.location}</div>
              <div className="flex gap-3"><DollarSign className="h-4 w-4 text-brand-teal" /> ${klass.price} per participant</div>
              <div className="flex gap-3"><Users className="h-4 w-4 text-brand-teal" /> {isFull ? "Class full — waitlist available" : `${klass.seats_available} of ${klass.capacity} seats available`}</div>
            </div>
            <div className="mt-6 pt-6 border-t border-white/20 text-xs text-white/60">
              Registration deadline: {new Date(klass.registration_deadline).toDateString()}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {deadlinePassed ? (
            <Card className="p-10 text-center">
              <h2 className="font-display text-2xl font-bold text-brand-coral">Registration closed</h2>
              <p className="mt-3 text-slate-600">The registration deadline has passed. Please check other upcoming classes.</p>
              <Link to="/classes"><Button className="mt-6 rounded-full">Browse Classes</Button></Link>
            </Card>
          ) : (
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-3xl p-8 space-y-5" data-testid="registration-form">
              <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">
                {isFull ? "Join the waitlist" : "Reserve your seat"}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full name *" required value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="reg-fullname" />
                <Field label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="reg-email" />
                <Field label="Phone *" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="reg-phone" />
                <Field label="Seats to reserve" type="number" min={1} max={10} value={form.seats} onChange={(v) => setForm({ ...form, seats: v })} testid="reg-seats" />
                <Field label="Employer / Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="reg-org" />
              </div>
              <div>
                <Label className="text-brand-navy font-semibold">Additional participants (if any)</Label>
                <Textarea rows={2} value={form.additional_participants} onChange={(e) => setForm({ ...form, additional_participants: e.target.value })} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid="reg-additional" />
              </div>
              <div>
                <Label className="text-brand-navy font-semibold">Accessibility needs or accommodations</Label>
                <Textarea rows={2} value={form.accessibility_needs} onChange={(e) => setForm({ ...form, accessibility_needs: e.target.value })} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid="reg-accessibility" />
              </div>
              <div className="flex items-start gap-3 p-4 bg-brand-surface rounded-xl">
                <Checkbox checked={form.policy_agreement} onCheckedChange={(v) => setForm({ ...form, policy_agreement: !!v })} data-testid="reg-policy" />
                <label className="text-sm text-slate-700 leading-relaxed">
                  I agree to the <Link to="/terms" className="text-brand-teal font-bold underline">cancellation and attendance policy</Link>. I understand payment is due at time of class unless otherwise arranged.
                </label>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-brand-coral hover:bg-brand-coral-dark text-white rounded-full py-6 font-bold text-base btn-lift" data-testid="reg-submit">
                {submitting ? "Submitting..." : isFull ? "Join Waitlist" : "Register Now"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, ...rest }) {
  return (
    <div>
      <Label className="text-brand-navy font-semibold">{label}</Label>
      <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid={testid} {...rest} />
    </div>
  );
}
